/**
 * useAudience.js — Hook + helpers pour mémoriser l'audience choisie.
 *
 * 3 audiences possibles :
 *   - 'eleve'           : élève qui apprend à rouler (cible principale)
 *   - 'enseignant'      : enseignant certifié CCS 2 Roues (outils terrain)
 *   - 'futur-enseignant': candidat préparant le CCS 2 Roues (hub /preparation-ccs)
 *
 * Stockage : localStorage clé `c2r:audience:v1`.
 * Pas d'envoi serveur — choix 100% local et non-bloquant (pas d'auth requise).
 *
 * Usage :
 *   const { audience, setAudience } = useAudience();
 *   setAudience('futur-enseignant'); // au clic d'une carte audience
 */
import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'c2r:audience:v1';

export const AUDIENCES = {
  ELEVE: 'eleve',
  ENSEIGNANT: 'enseignant',
  FUTUR_ENSEIGNANT: 'futur-enseignant',
};

export const AUDIENCE_META = {
  [AUDIENCES.ELEVE]: {
    label: 'Élève',
    accent: '#cf2031',
    homePath: '/parcours',
  },
  [AUDIENCES.ENSEIGNANT]: {
    label: 'Enseignant certifié',
    accent: '#0891b2',
    homePath: '/enseignant',
  },
  [AUDIENCES.FUTUR_ENSEIGNANT]: {
    label: 'Futur enseignant',
    accent: '#ea580c',
    homePath: '/preparation-ccs',
  },
};

function readAudience() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Object.values(AUDIENCES).includes(data.audience)) return data.audience;
    return null;
  } catch {
    return null;
  }
}

function writeAudience(audience) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ audience, updatedAt: new Date().toISOString() }),
    );
    // Évène pour mettre à jour d'autres onglets/composants
    window.dispatchEvent(new CustomEvent('c2r:audience-change', { detail: { audience } }));
  } catch {
    /* ignore */
  }
}

export default function useAudience() {
  const [audience, setAudienceState] = useState(() => readAudience());

  // Sync inter-tab / cross-component
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.audience !== undefined) setAudienceState(e.detail.audience);
    };
    const storageHandler = (e) => {
      if (e.key === STORAGE_KEY) setAudienceState(readAudience());
    };
    window.addEventListener('c2r:audience-change', handler);
    window.addEventListener('storage', storageHandler);
    return () => {
      window.removeEventListener('c2r:audience-change', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }, []);

  const setAudience = useCallback((value) => {
    if (!Object.values(AUDIENCES).includes(value)) return;
    writeAudience(value);
    setAudienceState(value);
  }, []);

  const clearAudience = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(new CustomEvent('c2r:audience-change', { detail: { audience: null } }));
    } catch {
      /* ignore */
    }
    setAudienceState(null);
  }, []);

  return { audience, setAudience, clearAudience, meta: audience ? AUDIENCE_META[audience] : null };
}
